import triggerOptionValues, {
  isAnyGreater,
  isAnyLess,
} from 'config/postLogicTriggerValues';
import { flow } from 'lodash';

import {
  filterEqualPositions,
  filterEqualRightPositions,
  addItemLeft,
  addItemRight,
  dropFirstItem,
  dropLastItem,
} from './listComparisonUtils';

export const ruleMatchMap = {
  [triggerOptionValues.equal]: (userAnswer, logicTriggerValue) =>
    userAnswer.toString() === logicTriggerValue.toString(),
  [triggerOptionValues.less]: (userAnswer, logicTriggerValue) =>
    userAnswer < logicTriggerValue,
  [triggerOptionValues.greater]: (userAnswer, logicTriggerValue) =>
    userAnswer > logicTriggerValue,
  [triggerOptionValues.lessOrEqual]: (userAnswer, logicTriggerValue) =>
    userAnswer <= logicTriggerValue,
  [triggerOptionValues.greaterOrEqual]: (userAnswer, logicTriggerValue) =>
    userAnswer >= logicTriggerValue,
};

const ruleFilterMap = {
  [triggerOptionValues.equal]: (answer, optionValue) =>
    optionValue.toString() !== answer.toString(),
  [triggerOptionValues.less]: (answer, optionValue) => optionValue >= answer,
  [triggerOptionValues.greater]: (answer, optionValue) => optionValue <= answer,
  [triggerOptionValues.lessOrEqual]: (answer, optionValue) =>
    optionValue > answer,
  [triggerOptionValues.greaterOrEqual]: (answer, optionValue) =>
    optionValue < answer,
};

const filterOptionsByLessTrigger = (allOptions, options) =>
  flow([
    filterEqualPositions(allOptions),
    addItemRight(allOptions),
    dropFirstItem(allOptions),
  ])(options);

const filterOptionsByGreaterTrigger = (allOptions, options) =>
  flow([
    filterEqualRightPositions(allOptions),
    addItemLeft(allOptions),
    dropLastItem(allOptions),
  ])(options);

const triggerFilterMap = {
  [triggerOptionValues.equal]: (allOptions, options) => options,
  [triggerOptionValues.lessOrEqual]: filterEqualPositions,
  [triggerOptionValues.greaterOrEqual]: filterEqualRightPositions,
  [triggerOptionValues.less]: filterOptionsByLessTrigger,
  [triggerOptionValues.greater]: filterOptionsByGreaterTrigger,
};

const isAnyGreaterInRuleList = rules =>
  rules.find(rule => isAnyGreater(rule.trigger));

const isAnyLessInRuleList = rules =>
  rules.find(rule => isAnyLess(rule.trigger));

const filterOptionsByRule = (rule, options) => {
  const ruleFilterFn = ruleFilterMap[rule.trigger];
  return options.filter(option => ruleFilterFn(rule.answer, option.value));
};

const filterOptionsByRules = (options, existedRules) => {
  if (!existedRules.length) {
    return options;
  }

  const [rule, ...nextRules] = existedRules;
  const filteredOptions = filterOptionsByRule(rule, options);

  return filterOptionsByRules(filteredOptions, nextRules);
};

const filterOptionsByTrigger = (allOptions, selectedTrigger, options) => {
  const triggerFilterFn = triggerFilterMap[selectedTrigger];

  return triggerFilterFn ? triggerFilterFn(allOptions, options) : options;
};

const filterAvailableLogicAnswers = (
  existedRules,
  allOptions,
  selectedTrigger,
) => {
  if (isAnyGreater(selectedTrigger) && isAnyGreaterInRuleList(existedRules)) {
    return [];
  }

  if (isAnyLess(selectedTrigger) && isAnyLessInRuleList(existedRules)) {
    return [];
  }

  const optionsFilteredByRules = filterOptionsByRules(allOptions, existedRules);
  return filterOptionsByTrigger(
    allOptions,
    selectedTrigger,
    optionsFilteredByRules,
  );
};

export default filterAvailableLogicAnswers;
